import { useEffect, useRef, useState } from "react";
import styles from "../input/inputComponent.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { GeoService } from "../../../services/geo.service";

type GeoItem = {
  ID: string;
  candidate: string;
  candidateAZ?: string;
  description?: string;
  descriptionAZ?: string;
  city?: string;
  cityAZ?: string;
  X: string; // longitude
  Y: string; // latitude
  type?: string;
};

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  isAddressSelected,
  isMarkerMoved,
  lang = "az",
  minChars = 3,
  limit = 5,
  placeholder = "Start typing…",
}: {
  value: string;
  onChange: (v: string) => void;
  isAddressSelected: boolean;
  isMarkerMoved: boolean;
  onSelect: (s: {
    address: string;
    lat: number;
    lon: number;
    raw: { address: { city: string } };
  }) => void;
  lang?: string;
  countryCodes?: string;
  viewbox?: [number, number, number, number];
  bounded?: boolean;
  minChars?: number;
  limit?: number;
  placeholder?: string;
  className?: string;
}) {
  const { data } = useSelector((state: RootState) => state.translationData);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<GeoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (value.trim().length < minChars) {
      setItems([]);
      setOpen(false);
      return;
    }

    if (!isAddressSelected && isMarkerMoved) {
      setItems([]);
      setOpen(false);
      return;
    }

    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await GeoService.autocomplete(value.trim(), lang);
        const list: GeoItem[] = res.data?.data?.Response ?? [];
        setItems(list.slice(0, limit));
        setOpen(list.length > 0);
      } catch {
        // network error — silent
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, lang, minChars, limit, isAddressSelected, isMarkerMoved]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (item: GeoItem) => {
    const address = item.candidateAZ || item.candidate;
    const city = item.cityAZ || item.city || "";
    onSelect({
      address,
      lat: parseFloat(item.Y),
      lon: parseFloat(item.X),
      raw: { address: { city } },
    });
    setOpen(false);
  };

  return (
    <div className={styles.input_container} style={{ marginTop: "20px" }}>
      <label>{data?.auth_courier_delivery_address}</label>
      <div ref={rootRef} className={styles.input_control} style={{ position: "relative" }}>
        <input
          style={{ zIndex: 9999 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          onKeyDown={(e) => {
            if (!open) return;
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
            if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(items[active]); }
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {open && (
          <div
            style={{
              position: "absolute",
              zIndex: 999999,
              top: "60px",
              left: 0,
              right: 0,
              marginTop: 4,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 10px 30px rgba(0,0,0,.08)",
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {loading && (
              <div style={{ padding: 12, fontSize: 14 }}>Axtarılır…</div>
            )}
            {!loading && items.length === 0 && (
              <div style={{ padding: 12, fontSize: 14 }}>Nəticə tapılmadı</div>
            )}
            {!loading &&
              items.map((it, i) => (
                <div
                  key={it.ID}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(it)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: i === active ? "#f3f4f6" : "white",
                    borderBottom: "1px solid #f3f4f6",
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                >
                  {(it.descriptionAZ || it.description) && (
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      {it.descriptionAZ || it.description}
                    </div>
                  )}
                  <div style={{ color: "#555" }}>
                    {it.candidateAZ || it.candidate}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
